import React, { useEffect, useState } from "react";
import { db } from "@/config/firebase";
import { collection, getDocs, query, where, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function ShowerGelMVP() {
  const { user } = useAuth();
  const [mvpData, setMvpData] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load all shower gel MVP votes
      const votesSnap = await getDocs(
        query(collection(db, "shower_gel_mvp"), orderBy("created_at", "desc"))
      );
      const votes = votesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMvpData(votes);

      // Load users
      const usersSnap = await getDocs(collection(db, "users"));
      const usersList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(usersList);
    } catch (error) {
      console.error("Error loading MVP data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Count votes per player
  const voteCounts = mvpData.reduce((acc, vote) => {
    acc[vote.player_id] = (acc[vote.player_id] || 0) + 1;
    return acc;
  }, {});

  // Sort by votes
  const sortedPlayers = Object.entries(voteCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([playerId, count]) => {
      const user = users.find(u => u.auth_uid === playerId || u.uid === playerId);
      return {
        playerId,
        name: user?.display_name || user?.name || 'Unbekannt',
        votes: count
      };
    });

  // Prepare chart data (last 10 events)
  const recentVotes = mvpData.slice(0, 10).reverse();
  const chartData = {
    labels: recentVotes.map((v, i) => `Event ${i + 1}`),
    datasets: sortedPlayers.slice(0, 5).map((player, idx) => {
      const colors = [
        'rgb(255, 99, 132)',
        'rgb(54, 162, 235)',
        'rgb(255, 205, 86)',
        'rgb(75, 192, 192)',
        'rgb(153, 102, 255)'
      ];
      
      return {
        label: player.name,
        data: recentVotes.map(vote => vote.player_id === player.playerId ? 1 : 0),
        borderColor: colors[idx],
        backgroundColor: colors[idx].replace('rgb', 'rgba').replace(')', ', 0.5)'),
        tension: 0.4
      };
    })
  };

  if (loading) {
    return (
      <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6 flex items-center justify-center">
        <p>Lädt...</p>
      </div>
    );
  }

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        🚿 Dusch-Gel MVP Award
      </h2>

      {/* Leaderboard Table */}
      <Card className="bg-[#1a0d11] border-white/10 mb-6">
        <CardContent className="pt-4">
          <h3 className="font-bold mb-3">🏆 Ewige Bestenliste</h3>
          <div className="space-y-2">
            {sortedPlayers.length === 0 ? (
              <p className="text-sm text-rose-200/60">Noch keine Votes vorhanden</p>
            ) : (
              sortedPlayers.map((player, index) => (
                <div
                  key={player.playerId}
                  className="flex items-center justify-between p-3 rounded bg-[#160b0f] border border-white/10"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold">
                      {index === 0 && '🥇'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      {index > 2 && `${index + 1}.`}
                    </span>
                    <span>{player.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🚿</span>
                    <span className="font-bold text-lg">{player.votes}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      {sortedPlayers.length > 0 && recentVotes.length > 0 && (
        <Card className="bg-[#1a0d11] border-white/10">
          <CardContent className="pt-4">
            <h3 className="font-bold mb-3">📊 Verlauf (letzte 10 Events)</h3>
            <div className="bg-white/5 p-4 rounded">
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: { color: '#fff' }
                    },
                    title: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: { color: '#fff', stepSize: 1 },
                      grid: { color: 'rgba(255,255,255,0.1)' }
                    },
                    x: {
                      ticks: { color: '#fff' },
                      grid: { color: 'rgba(255,255,255,0.1)' }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
